import { Page } from '@app/components/page/Page';
import { DeleteButton } from '@app/modules/admin/components/DeleteButton';
import { EditButton } from '@app/modules/admin/components/EditButton';
import { Box, Stack } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { ReactNode } from 'react';

export interface ResourceViewPageHeaderConfig<TResource> {
  icon?: ReactNode;
  title: (resource: TResource) => ReactNode;
  subtitle?: (resource: TResource) => ReactNode;
  breadcrumbs?: (resource: TResource) => ReactNode;
}

export interface ResourceViewPageDeleteConfig<TResource> {
  onDelete: (resource: TResource) => Promise<void>;
  title?: (resource: TResource) => string;
  message: (resource: TResource) => ReactNode;
  successMessage?: (resource: TResource) => ReactNode;
  redirectTo?: string;
  disabled?: (resource: TResource) => boolean;
}

export interface ResourceViewPageEditConfig<TResource = unknown> {
  to: (resource: TResource) => string;
}

export interface ResourceViewPageProps<TResource = unknown> {
  query: UseQueryResult<TResource>;
  header: ResourceViewPageHeaderConfig<TResource>;
  htmlTitle?: (resource: TResource) => string;
  dataCy?: string;
  editConfig?: ResourceViewPageEditConfig<TResource>;
  deleteConfig?: ResourceViewPageDeleteConfig<TResource>;
  customActions?: (resource: TResource) => ReactNode;
  children: (resource: TResource) => ReactNode;
}

export function ResourceViewPage<TResource = unknown>({
  query,
  header,
  htmlTitle,
  dataCy,
  editConfig,
  deleteConfig,
  customActions,
  children,
}: ResourceViewPageProps<TResource>) {
  return (
    <QueryView query={query}>
      {(resource: TResource) => {
        const actions = (
          <Stack direction="row" spacing={1}>
            {editConfig && <EditButton to={editConfig.to(resource)} />}
            {deleteConfig && (
              <DeleteButton
                onDelete={() => deleteConfig.onDelete(resource)}
                title={deleteConfig.title?.(resource)}
                message={deleteConfig.message(resource)}
                successMessage={deleteConfig.successMessage?.(resource)}
                redirectTo={deleteConfig.redirectTo}
                disabled={deleteConfig.disabled?.(resource)}
              />
            )}
            {customActions?.(resource)}
          </Stack>
        );

        const titleNode = header.title(resource);
        const breadcrumbsNode = header.breadcrumbs?.(resource);
        const subtitleNode = header.subtitle?.(resource);
        const headerNode = breadcrumbsNode ? (
          <Page.Header
            icon={header.icon}
            title={
              subtitleNode ? (
                <>
                  {titleNode}
                  <Box component="span" sx={{ opacity: 0.7 }}>
                    {' '}
                    — {subtitleNode}
                  </Box>
                </>
              ) : (
                titleNode
              )
            }
            breadcrumbs={breadcrumbsNode}
            actions={actions}
          />
        ) : (
          <Page.Header icon={header.icon} title={titleNode} actions={actions} />
        );

        return (
          <Page data-cy={dataCy} htmlTitle={htmlTitle?.(resource)}>
            {headerNode}
            <Page.Content>{children(resource)}</Page.Content>
          </Page>
        );
      }}
    </QueryView>
  );
}
